import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const SecurityAuditLog = () => {
  const [filterType, setFilterType] = useState('all');
  const [timeRange, setTimeRange] = useState('7days');

  const auditEvents = [
    {
      id: 1,
      type: 'login',
      icon: 'LogIn',
      title: 'Successful Login',
      description: 'Login from MacBook Pro 16" in San Francisco, CA',
      timestamp: '2026-01-12 16:45:23',
      status: 'success',
      ipAddress: '192.168.1.105',
      device: 'Chrome 120.0 on macOS'
    },
    {
      id: 2,
      type: 'security',
      icon: 'ShieldCheck',
      title: 'Two-Factor Authentication Enabled',
      description: 'Authenticator app configured successfully',
      timestamp: '2026-01-12 14:30:15',
      status: 'success',
      ipAddress: '192.168.1.105',
      device: 'Chrome 120.0 on macOS'
    },
    {
      id: 3,
      type: 'device',
      icon: 'Smartphone',
      title: 'New Device Added',
      description: 'iPhone 15 Pro registered as trusted device',
      timestamp: '2026-01-11 09:20:45',
      status: 'success',
      ipAddress: '192.168.1.108',
      device: 'Safari Mobile on iOS'
    },
    {
      id: 4,
      type: 'login',
      icon: 'AlertTriangle',
      title: 'Failed Login Attempt',
      description: 'Incorrect password entered from unknown location',
      timestamp: '2026-01-10 22:15:30',
      status: 'warning',
      ipAddress: '203.45.67.89',
      device: 'Chrome 119.0 on Windows'
    },
    {
      id: 5,
      type: 'password',
      icon: 'Key',
      title: 'Password Changed',
      description: 'Account password updated successfully',
      timestamp: '2026-01-10 18:45:12',
      status: 'success',
      ipAddress: '192.168.1.105',
      device: 'Chrome 120.0 on macOS'
    },
    {
      id: 6,
      type: 'security',
      icon: 'Shield',
      title: 'Security Settings Updated',
      description: 'Login notification preferences modified',
      timestamp: '2026-01-09 15:30:00',
      status: 'success',
      ipAddress: '192.168.1.105',
      device: 'Chrome 120.0 on macOS'
    },
    {
      id: 7,
      type: 'device',
      icon: 'Monitor',
      title: 'Device Removed',
      description: 'Windows Desktop removed from trusted devices',
      timestamp: '2026-01-08 11:20:45',
      status: 'success',
      ipAddress: '192.168.1.105',
      device: 'Chrome 120.0 on macOS'
    },
    {
      id: 8,
      type: 'login',
      icon: 'LogOut',
      title: 'Session Expired',
      description: 'Automatic logout due to inactivity',
      timestamp: '2026-01-07 23:45:30',
      status: 'info',
      ipAddress: '192.168.1.108',
      device: 'Safari Mobile on iOS'
    }
  ];

  const eventTypeOptions = [
    { value: 'all', label: 'All Events' },
    { value: 'login', label: 'Login Events' },
    { value: 'security', label: 'Security Changes' },
    { value: 'device', label: 'Device Management' },
    { value: 'password', label: 'Password Changes' }
  ];

  const timeRangeOptions = [
    { value: '24hours', label: 'Last 24 Hours' },
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'bg-success/10 text-success border-success/20';
      case 'warning':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'error':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return 'CheckCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'error':
        return 'XCircle';
      default:
        return 'Info';
    }
  };

  const filteredEvents = filterType === 'all' 
    ? auditEvents 
    : auditEvents?.filter(event => event?.type === filterType);

  return (
    <div className="bg-card rounded-lg shadow-md p-4 md:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={24} color="var(--color-accent)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-foreground">Security Audit Log</h2>
            <p className="text-sm text-muted-foreground">Track all account activity and security events</p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Select
          label="Event Type"
          options={eventTypeOptions}
          value={filterType}
          onChange={setFilterType}
        />
        <Select
          label="Time Range"
          options={timeRangeOptions}
          value={timeRange}
          onChange={setTimeRange}
        />
      </div>
      <div className="space-y-3">
        {filteredEvents?.map((event) => (
          <div key={event?.id} className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getStatusColor(event?.status)}`}>
                <Icon name={event?.icon} size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-medium text-foreground mb-1">{event?.title}</h3>
                    <p className="text-sm text-muted-foreground break-words">{event?.description}</p>
                  </div>
                  <div className={`px-2 py-1 rounded-full flex items-center gap-1 flex-shrink-0 ${getStatusColor(event?.status)}`}>
                    <Icon name={getStatusIcon(event?.status)} size={12} />
                    <span className="text-xs font-medium capitalize">{event?.status}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="Clock" size={12} />
                    <span className="truncate">{event?.timestamp}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="Network" size={12} />
                    <span className="truncate">{event?.ipAddress}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="Monitor" size={12} />
                    <span className="truncate">{event?.device}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 pt-6 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Showing {filteredEvents?.length} of {auditEvents?.length} events
        </p>
        <div className="flex gap-2">
          <Button variant="outline" iconName="Download" iconPosition="left" size="sm">
            Export Log
          </Button>
          <Button variant="outline" iconName="RefreshCw" iconPosition="left" size="sm">
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SecurityAuditLog;